import { useState } from 'react';
import debounce from 'lodash/debounce';
import { t } from 'ttag';

import { DeliveryService } from '@newModels';
import {
  SelectAsync,
  SelectOption,
  SelectProps,
  InputActionMeta,
} from '@ui/Select';
import {
  useDeliveryCitySelectLazyQuery,
  DeliveryCitySelectQuery,
} from '@containers/DeliveryCitySelect/queries/graphql.v4.generated';

// constants
const minSearchStringLength = 2;

// 0.3s
const DEBOUNCE_SEARCH_DELAY = 300;

const convertCitiesToOptions = (cities?: DeliveryCitySelectQuery['orderCities']) => (
  cities
    ? cities.map((city) => ({ label: city.name, value: city.id }))
    : []
);

export type DeliveryCitySelectOption = SelectOption<string>;

interface Props extends Omit<SelectProps<DeliveryCitySelectOption>, 'options'> {
  regionId?: string,
  deliveryService?: DeliveryService;
}

export const DeliveryCitySelect: React.FC<Props> = ({
  regionId,
  deliveryService,
  ...props
}) => {
  const { value, onChange } = props;
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [getCities] = useDeliveryCitySelectLazyQuery({
    context: {
      v4: true,
    },
    fetchPolicy: 'network-only',
  });

  const loadOptions = async (
    searchString: string,
    callback: (options: SelectOption<string>[]) => void,
  ) => {
    if (deliveryService && searchString.length >= minSearchStringLength) {
      try {
        const { data } = await getCities({
          variables: {
            deliveryService,
            regionId,
            cityName: searchString,
          },
        });

        const options = convertCitiesToOptions(data?.orderCities);
        callback(options);
      } catch {
        callback([]);
      }
    } else {
      callback([]);
    }
  };

  const loadOptionsDebounce = debounce(loadOptions, DEBOUNCE_SEARCH_DELAY);

  const onInputChange = (searchString: string, actionMeta: InputActionMeta) => {
    setInputValue(searchString);

    // Clear selected city if user clear input string
    if (onChange && actionMeta.action === 'input-change' && !searchString) {
      onChange(null, { action: 'clear', removedValues: [value as SelectOption<string>] });
    }

    return searchString;
  };

  const handleFocus = () => {
    setInputValue((value as SelectOption<string> | undefined)?.label || '');
  };

  return (
    (
      <SelectAsync
        {...props}
        blurInputOnSelect={true}
        inputValue={inputValue}
        placeholder={t`Ввести місто`}
      // @ts-ignore
        loadOptions={loadOptionsDebounce}
        isSearchable={true}
        isMulti={false}
        isClearable={true}
        menuIsOpen={inputValue ? menuIsOpen : false}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        onFocus={handleFocus}
        openMenuOnClick={false}
        onInputChange={onInputChange}
      />
    )
  );
};
